class VideoSection {
	constructor() {
		this.observer = null;
		this.init();
	}

	init() {
		// Wait a bit to ensure custom elements are defined
		setTimeout(() => {
			this.setupObserver();
		}, 100);
	}

	setupObserver() {
		// Find all deferred-media elements that should autoplay on scroll
		const deferredMediaElements = document.querySelectorAll(
			'deferred-media[data-autoplay-on-scroll="true"]'
		);

		if (deferredMediaElements.length === 0) {
			return;
		}

		// Create Intersection Observer
		const observerOptions = {
			root: null,
			rootMargin: "100px", // Start loading 100px before video enters viewport
			threshold: 0.25, // Trigger when 25% of video is visible
		};

		this.observer = new IntersectionObserver((entries) => {
			entries.forEach((entry) => {
				const deferredMedia = entry.target;

				if (entry.isIntersecting) {
					// Only load if not already loaded
					if (!deferredMedia.hasAttribute("loaded")) {
						// Hide the poster button immediately so it doesn't block the video
						const posterButton = deferredMedia.querySelector(
							'[id^="Deferred-Poster-"]'
						);
						if (posterButton) {
							posterButton.style.display = "none";
							posterButton.style.opacity = "0";
							posterButton.style.pointerEvents = "none";
						}

						// Set up mutation observer to watch for video element being added
						const mutationObserver = new MutationObserver((mutations, obs) => {
							const videoElement = deferredMedia.querySelector("video");
							if (videoElement) {
								obs.disconnect();

								// Ensure poster stays hidden
								if (posterButton) {
									posterButton.style.display = "none";
								}

								this.playVideo(videoElement, deferredMedia, posterButton);
							}
						});

						mutationObserver.observe(deferredMedia, {
							childList: true,
							subtree: true,
						});

						// Load the video content
						if (typeof deferredMedia.loadContent === "function") {
							deferredMedia.loadContent(false);
						} else {
							// Fallback: trigger click on poster button if loadContent method not available
							if (posterButton) {
								posterButton.click();
							}
						}

						// Also use a fallback timeout check
						let retryCount = 0;
						const maxRetries = 100;

						const checkVideoAndPlay = () => {
							const videoElement = deferredMedia.querySelector("video");
							if (videoElement) {
								mutationObserver.disconnect();

								// Ensure poster stays hidden
								if (posterButton) {
									posterButton.style.display = "none";
								}

								this.playVideo(videoElement, deferredMedia, posterButton);
							} else if (retryCount < maxRetries) {
								retryCount++;
								setTimeout(checkVideoAndPlay, 100);
							} else {
								mutationObserver.disconnect();
							}
						};

						// Start checking after a small delay to allow loadContent to execute
						setTimeout(checkVideoAndPlay, 50);
					}
				} else {
					// Optional: Pause video when it goes out of view
					const videoElement = deferredMedia.querySelector("video");
					if (videoElement && !videoElement.paused) {
						videoElement.pause();
					}
				}
			});
		}, observerOptions);

		// Observe all deferred-media elements
		deferredMediaElements.forEach((element) => {
			this.observer.observe(element);
		});
	}

	playVideo(videoElement, deferredMedia, posterButton) {
		// Ensure poster button is hidden
		if (posterButton) {
			posterButton.style.display = "none";
			posterButton.style.opacity = "0";
			posterButton.style.pointerEvents = "none";
			posterButton.style.visibility = "hidden";
		}

		// Ensure video is muted for autoplay policies
		videoElement.muted = true;
		videoElement.setAttribute("muted", "");
		videoElement.setAttribute("playsinline", "");
		videoElement.setAttribute("autoplay", "");

		// Always enable looping for autoplay videos on scroll
		videoElement.loop = true;
		videoElement.setAttribute("loop", "");

		// Ensure video can autoplay
		videoElement.removeAttribute("preload");
		videoElement.preload = "auto";

		// Make sure video is visible and not behind anything
		videoElement.style.display = "block";
		videoElement.style.visibility = "visible";
		videoElement.style.opacity = "1";

		// Video should fill the parent container which already has the aspect ratio
		// Position absolutely to fill the container
		videoElement.style.position = "absolute";
		videoElement.style.top = "0";
		videoElement.style.left = "0";
		videoElement.style.width = "100%";
		videoElement.style.height = "100%";
		videoElement.style.objectFit = "cover";

		// Ensure parent has position relative for absolute positioning
		if (getComputedStyle(deferredMedia).position === "static") {
			deferredMedia.style.position = "relative";
		}

		// Play the video
		const playPromise = videoElement.play();
		if (playPromise !== undefined) {
			playPromise
				.then(() => {
					// Remove controls only after video successfully plays
					videoElement.removeAttribute("controls");
					videoElement.controls = false;

					// Ensure poster is completely hidden
					if (posterButton) {
						posterButton.style.display = "none";
						posterButton.style.visibility = "hidden";
						posterButton.remove();
					}

					// Stop observing once video is playing
					if (this.observer) {
						this.observer.unobserve(deferredMedia);
					}
				})
				.catch((error) => {
					// Try again after a short delay
					setTimeout(() => {
						videoElement
							.play()
							.then(() => {
								// Remove controls only after video successfully plays
								videoElement.removeAttribute("controls");
								videoElement.controls = false;

								if (posterButton) {
									posterButton.style.display = "none";
									posterButton.style.visibility = "hidden";
								}
							})
							.catch(() => {
								// Silent fail on retry
							});
					}, 500);
					// Still stop observing to avoid repeated attempts
					if (this.observer) {
						this.observer.unobserve(deferredMedia);
					}
				});
		} else {
			// Ensure poster is hidden even if play() doesn't return a promise
			if (posterButton) {
				posterButton.style.display = "none";
				posterButton.style.visibility = "hidden";
			}
			if (this.observer) {
				this.observer.unobserve(deferredMedia);
			}
		}
	}
}

// Initialize when DOM is ready and custom elements are defined
function initializeVideoSection() {
	// Wait for custom elements to be defined
	if (customElements.get("deferred-media")) {
		new VideoSection();
	} else {
		// If custom element not yet defined, wait for it
		customElements.whenDefined("deferred-media").then(() => {
			new VideoSection();
		});
	}
}

if (document.readyState === "loading") {
	document.addEventListener("DOMContentLoaded", () => {
		initializeVideoSection();
	});
} else {
	initializeVideoSection();
}

// Re-initialize for dynamically loaded content (e.g., theme editor, section reloads)
if (typeof Shopify !== "undefined" && Shopify.designMode) {
	document.addEventListener("shopify:section:load", () => {
		setTimeout(() => {
			initializeVideoSection();
		}, 100);
	});
}
